/**
 * SamFloatingTile — bouton flottant rond bottom-right.
 *
 * Pattern d'un widget de chat (style Intercom / HubSpot) : un cercle
 * coloré qu'on tape pour ouvrir la conversation. L'avatar Sam animé
 * vit à l'intérieur pour garder le côté gamifié.
 *
 * Halo + bounce sur chaque samNudgeKey pour signaler "Sam veut parler".
 *
 * Quand `peekText` est défini, affiche aussi une mini-bulle ancrée à
 * gauche du tile pour donner un aperçu du dernier message Sam (D-2
 * proactive teasers, mais aussi tout message reçu pendant que la sheet
 * est fermée). Tap sur la peek = même comportement que tap sur le tile.
 */
import React, { useEffect, useRef, useState } from "react"
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Animated as RNAnimated,
    Easing,
} from "react-native"
import Animated, { FadeIn, FadeOut } from "react-native-reanimated"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import { toRevealTokens } from "@common/components/richText/revealTokens"
import { theme } from "@common/theme/theme"
import { ChampionImageM } from "../../champions/ChampionImageM"

const TILE_SIZE = 72
const TILE_EDGE = 20

/**
 * La place qu'un contenu qui défile sous la tuile laisse à sa fin, pour que son dernier élément
 * (l'icône « Aa » d'un énoncé, ses flèches de question, une correction) remonte au-dessus d'elle.
 * Fournie par l'écran qui pose la tuile : ailleurs (live, activité sans Sam), elle vaut 0.
 */
export const SamTileClearance = React.createContext(0)
export const SAM_TILE_CLEARANCE = TILE_SIZE + TILE_EDGE
const PEEK_MAX_CHARS = 80

// L'aperçu est du texte simple : les formules passent en Unicode, comme dans la
// bulle, au lieu de montrer leurs `$` et leurs antislashs.
const truncatePeek = (content) => {
    if (!content) return ""
    const plain = toRevealTokens(String(content))
        .map((token) => token.value)
        .join("")
        .replace(/\*\*/g, "")
        .replace(/\s+/g, " ")
        .trim()
    if (plain.length <= PEEK_MAX_CHARS) return plain
    return plain.slice(0, PEEK_MAX_CHARS - 1).trimEnd() + "…"
}

const SamFloatingTile = ({
    onPress,
    samMood = "encouraging",
    samNudgeKey = 0,
    hasUnread = false,
    peekText = null,
    onDismissPeek,
}) => {
    const peekContent = truncatePeek(peekText)
    const haloAnim = useRef(new RNAnimated.Value(0)).current
    const bounceAnim = useRef(new RNAnimated.Value(0)).current
    const [animKey, setAnimKey] = useState(0)

    useEffect(() => {
        if (samNudgeKey === 0) return
        setAnimKey((k) => k + 1)
        haloAnim.setValue(0)
        bounceAnim.setValue(0)
        RNAnimated.parallel([
            RNAnimated.sequence([
                RNAnimated.timing(haloAnim, {
                    toValue: 1,
                    duration: 600,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: true,
                }),
                RNAnimated.timing(haloAnim, {
                    toValue: 0,
                    duration: 600,
                    useNativeDriver: true,
                }),
            ]),
            RNAnimated.sequence([
                RNAnimated.timing(bounceAnim, {
                    toValue: -6,
                    duration: 220,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: true,
                }),
                RNAnimated.timing(bounceAnim, {
                    toValue: 0,
                    duration: 280,
                    easing: Easing.bounce,
                    useNativeDriver: true,
                }),
            ]),
        ]).start()
    }, [samNudgeKey])

    const haloScale = haloAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.4],
    })
    const haloOpacity = haloAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 0.4],
    })

    return (
        <View style={styles.wrap} pointerEvents="box-none">
            {peekContent ? (
                <Animated.View
                    entering={FadeIn.duration(220)}
                    exiting={FadeOut.duration(180)}
                    style={styles.peekContainer}
                >
                    <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={onPress}
                        style={styles.peekBubble}
                    >
                        <TextM
                            fontSize={12}
                            color={theme.colors.dark}
                            wrap
                            style={{ lineHeight: 17, flexShrink: 1 }}
                        >
                            {peekContent}
                        </TextM>
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={onDismissPeek}
                        style={styles.peekDismiss}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        activeOpacity={0.7}
                    >
                        <FontAwesome5
                            name="times"
                            size={10}
                            color={theme.colors.grey}
                            solid
                        />
                    </TouchableOpacity>
                </Animated.View>
            ) : null}
            <View style={styles.tileBox}>
                <RNAnimated.View
                    pointerEvents="none"
                    style={[
                        styles.halo,
                        {
                            opacity: haloOpacity,
                            transform: [{ scale: haloScale }],
                        },
                    ]}
                />
                <RNAnimated.View
                    style={{ transform: [{ translateY: bounceAnim }] }}
                >
                    <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={onPress}
                        style={styles.bubble}
                        accessibilityRole="button"
                        accessibilityLabel={
                            hasUnread
                                ? "Ouvrir la conversation avec Sam, nouveau message"
                                : "Ouvrir la conversation avec Sam"
                        }
                    >
                        <ChampionImageM
                            name="sam"
                            // L'animation (un WebP de 2,3 Mo, 192 images) ne
                            // joue que le temps de l'aperçu : en boucle, elle
                            // décodait en continu sur l'écran où l'élève
                            // travaille. Le point rouge dit le reste.
                            animated={!!peekContent}
                            animationKey={`${samMood}-${animKey}`}
                            width={TILE_SIZE - 12}
                        />
                    </TouchableOpacity>
                </RNAnimated.View>
                {hasUnread && <View style={styles.unreadDot} />}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    // The wrapper is anchored bottom-right but lays out its children as
    // a row so the peek bubble can sit naturally to the LEFT of the
    // tile. No fixed width — it grows with the peek content.
    wrap: {
        position: "absolute",
        right: TILE_EDGE,
        // Assez haut pour dégager le bouton de soumission de
        // l'activité, ancré en bas et pleine largeur sur mobile : à
        // 24 px du bord, la tuile recouvrait son coin droit — le
        // bouton le plus important de l'écran.
        bottom: 96,
        flexDirection: "row",
        alignItems: "flex-end",
        zIndex: 10,
        elevation: 10,
    },
    tileBox: {
        width: TILE_SIZE,
        height: TILE_SIZE,
        alignItems: "center",
        justifyContent: "center",
    },
    halo: {
        position: "absolute",
        width: TILE_SIZE,
        height: TILE_SIZE,
        borderRadius: TILE_SIZE / 2,
        // La couleur de Sam, pas celle d'Abou. Le thème réserve une teinte à
        // chaque mascotte ; la tuile portait purple (#7A6EB5), si bien que Sam
        // n'avait aucune identité chromatique dans sa propre fonctionnalité.
        backgroundColor: theme.colors.sam,
    },
    bubble: {
        width: TILE_SIZE,
        height: TILE_SIZE,
        borderRadius: TILE_SIZE / 2,
        backgroundColor: theme.colors.sam,
        borderWidth: 2,
        borderColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 10,
        elevation: 8,
    },
    unreadDot: {
        position: "absolute",
        top: 2,
        right: 2,
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: theme.colors.error,
        borderWidth: 2,
        borderColor: theme.colors.white,
    },
    peekContainer: {
        flexDirection: "row",
        alignItems: "flex-end",
        maxWidth: 240,
        marginRight: 10,
        marginBottom: 8,
    },
    peekBubble: {
        flexShrink: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 12,
        paddingVertical: 9,
        borderRadius: 14,
        borderBottomRightRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.lightMore + "80",
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.16,
        shadowRadius: 10,
        elevation: 6,
    },
    peekDismiss: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 4,
        marginBottom: 4,
    },
})

export default SamFloatingTile
